import { ReactNode } from "react";
import { Route, Routes } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import AcceptInvitation from "./components/Invitation/AcceptInvitation";
import NewPatientSelfSignup from "./components/SignUp/NewPatientSelfSignup";
import LaunchWaitlist from "./components/SignUp/LaunchWaitlist";
import ResendPatientInvitation from "./components/SignUp/ResendPatientInvitation";
import AcceptInvitationNewPatientSelfSignup from "./components/Invitation/AcceptInvitationNewPatientSelfSignup";
import LoginRoute from "./components/Auth/LoginRoute";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ResetPassword from "./components/Auth/ResetPassword";
import SecureAccount from "./components/Auth/SecureAccount";
import { isModuleEnabled } from "./modules/activeModules";
import { isSignupBlocked } from "./config/runtimeConfig";
import { useBranding } from "./branding/BrandingProvider";
import { brandPrefersDark, getBrandThemes } from "./theme";

/**
 * TeleHeadache's signed-out pages — sign-in, signup, invitations, password reset — are always
 * light, whatever dark-mode preference this device saved (Derek, 2026-10-02). The preference
 * still applies once signed in. CerebraLinked is untouched: these pages keep following the
 * preference, which defaults to dark there, and its sign-in page pins dark itself.
 *
 * CssBaseline is repeated so the body behind these pages turns light too; the app-level one
 * paints it from the outer, preference-driven theme.
 */
function SignedOutTheme({ children }: { children: ReactNode }) {
  const brand = useBranding();
  if (brandPrefersDark(brand.key)) return <>{children}</>;
  return (
    <ThemeProvider theme={getBrandThemes(brand.key).light}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

const UnauthenticatedRouting = () => {
  return (
    <SignedOutTheme>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* "Wasn't you? Secure my account" from the new-device sign-in email. */}
        <Route path="/secure-account/:token" element={<SecureAccount />} />
        {/* Pre-launch (runConfig.signupBlocked): the signup link the marketing site points at
            collects an email for the launch announcement instead, and the resend page has nothing
            to resend. The API refuses both funnels on its own (MTC:Signup:Blocked). */}
        {isSignupBlocked() ? (
          <Route path="/i/new-patient" element={<LaunchWaitlist />} />
        ) : (
          isModuleEnabled("patient-self-signup") && (
            <Route
              path="/i/new-patient"
              element={<NewPatientSelfSignup />}
            ></Route>
          )
        )}
        {isModuleEnabled("patient-self-signup") && !isSignupBlocked() && (
          <Route
            path="/i/resend-invitation"
            element={<ResendPatientInvitation />}
          />
        )}
        <Route
          path="/i/p/:invitationId"
          element={<AcceptInvitationNewPatientSelfSignup />}
        ></Route>
        <Route path="/i/:invitationId" element={<AcceptInvitation />}></Route>
        <Route path="*" element={<LoginRoute />} />
      </Routes>
    </SignedOutTheme>
  );
};

export default UnauthenticatedRouting;
